"use client";

import { useState } from "react";
import { ChevronDown, Gift, Layers, Receipt } from "lucide-react";
import { Section, Typography } from "@/components/ui";

const quickAnswers = [
  {
    id: "trial",
    icon: Gift,
    question: "Is there a free trial?",
    answer:
      "Yes — every new account starts with a 14-day free trial. No credit card required. Your configured plan and screen count are pre-loaded when you sign up.",
  },
  {
    id: "enterprise",
    icon: Layers,
    question: "What happens above 500 screens?",
    answer:
      "Deployments above 500 screens move to our Enterprise tier with custom pricing, dedicated support, and SLA guarantees. Our sales team will build a tailored proposal for your scale.",
  },
  {
    id: "gst",
    icon: Receipt,
    question: "Is GST included in the price shown?",
    answer:
      "Prices shown are exclusive of GST. For India-based customers, 18% GST is added at checkout and shown separately in your summary above.",
  },
];

export function QuickAnswersSection() {
  const [openId, setOpenId] = useState<string | null>(quickAnswers[0]?.id ?? null);

  return (
    <Section variant="default" className="py-12 md:py-16 bg-linear-to-b from-slate-50 to-white">
      <div className="container mx-auto px-8 xl:px-0">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <span className="section-label">Quick answers</span>
            <Typography variant="h3" as="h2" className="mt-3">
              Common pricing questions
            </Typography>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden divide-y divide-gray-100">
            {quickAnswers.map(item => {
              const Icon = item.icon;
              const isOpen = openId === item.id;

              return (
                <div key={item.id} className={isOpen ? "bg-primary/3" : "bg-white"}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-4 px-5 py-4 md:px-6 md:py-5 text-left transition-colors hover:bg-gray-50/80"
                    onClick={() => setOpenId(isOpen ? null : item.id)}
                    aria-expanded={isOpen}
                  >
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                        isOpen ? "bg-primary text-white border-primary" : "bg-[#F8F9FF] text-primary border-[#DDE0E9]"
                      }`}
                    >
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>

                    <span className={`flex-1 font-semibold text-sm md:text-base ${isOpen ? "text-primary" : "text-text-primary"}`}>
                      {item.question}
                    </span>

                    <ChevronDown
                      className={`w-5 h-5 shrink-0 text-text-secondary transition-transform duration-200 ${
                        isOpen ? "rotate-180 text-primary" : ""
                      }`}
                      aria-hidden="true"
                    />
                  </button>

                  <div
                    className={`grid transition-all duration-200 ease-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 md:px-6 md:pb-6 md:pl-[5.5rem] text-sm text-text-secondary leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
}